import moment from 'moment';
import {
  AccountData,
  AreaOccupancyData,
  OrderPageData,
  ResourceCalculationMode,
  SchedulerConfigurationData,
  WorkDayType,
  WorkingDaysData,
  WorkingHoursData,
} from '../../../api';
import { getOrderPage } from '../../../utils/AccountUtils';
import { masksIntersect } from '../../../utils/maskUtils';
import { SECONDS_PER_MINUTE } from './occupancyMath';

export type DayBounds = { startSeconds: number; endSeconds: number };

/** Fallback session window when no page duration can be resolved: 2 hours. */
export const FALLBACK_SESSION_MINUTES = 120;

const DAY_NAMES: (keyof WorkingDaysData)[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const configOrderPages = (config: SchedulerConfigurationData, account: AccountData | undefined): OrderPageData[] =>
  (config.allowed_masks ?? [])
    .map((allowedMask) => getOrderPage(account, allowedMask.order_page_id) as OrderPageData | undefined)
    .filter((page): page is OrderPageData => Boolean(page));

export const workingHoursForDate = (page: OrderPageData, account: AccountData | undefined, date: string) => {
  const dayName = DAY_NAMES[moment(date).day()];
  const experienceWorkingDays = (account?.experiences?.[0] as { working_days?: WorkingDaysData } | undefined)
    ?.working_days;
  const workingDays = page.working_days ?? experienceWorkingDays;
  return workingDays?.[dayName];
};

const boundsOfWorkingHours = (hours: WorkingHoursData, pageDurationSeconds: number): DayBounds | null => {
  if (hours.work_day_type === WorkDayType.NOT_WORKING) {
    return null;
  }
  if (hours.work_day_type === WorkDayType.INTERVAL) {
    if (hours.from_time === undefined || hours.to_time === undefined) {
      return null;
    }
    return { startSeconds: hours.from_time, endSeconds: hours.to_time };
  }
  const starts = hours.start_seconds ?? [];
  if (starts.length === 0) {
    return null;
  }
  return {
    startSeconds: Math.min(...starts),
    endSeconds: Math.max(...starts) + pageDurationSeconds,
  };
};

/**
 * Day bounds for the horizon and the timeline strip: the client-side union of the opening hours of
 * the config's order pages on that weekday. Nothing here is hardcoded — an account with no usable
 * hours returns null and the caller falls back to the slots the server sent.
 */
export const configDayBounds = (
  config: SchedulerConfigurationData,
  account: AccountData | undefined,
  date: string,
): DayBounds | null => {
  const bounds = configOrderPages(config, account)
    .map((page) => {
      const hours = workingHoursForDate(page, account, date);
      return hours ? boundsOfWorkingHours(hours, page.duration ?? 0) : null;
    })
    .filter((value): value is DayBounds => Boolean(value));

  if (bounds.length === 0) {
    return null;
  }
  return {
    startSeconds: Math.min(...bounds.map((bound) => bound.startSeconds)),
    endSeconds: Math.max(...bounds.map((bound) => bound.endSeconds)),
  };
};

/**
 * Day bounds of one area: the hours of the pages that can actually book it, walk-in pages first (a
 * static group page locks the whole room and its hours say nothing about walk-ins). A config's other
 * pages are left out, so a group page that runs late does not stretch the indoor strip past closing.
 * Falls back to the config's union, then null.
 */
export const areaDayBounds = (
  area: AreaOccupancyData,
  config: SchedulerConfigurationData | undefined,
  account: AccountData | undefined,
  date: string,
): DayBounds | null => {
  if (!config) {
    return null;
  }
  const intersecting = (config.allowed_masks ?? []).filter((allowedMask) =>
    masksIntersect(allowedMask.mask, area.mask),
  );
  const bookable = intersecting.filter(
    (allowedMask) => allowedMask.resource_calculation_mode !== ResourceCalculationMode.STATIC,
  );
  const candidates = bookable.length > 0 ? bookable : intersecting;
  const bounds = candidates
    .map((allowedMask) => getOrderPage(account, allowedMask.order_page_id) as OrderPageData | undefined)
    .filter((page): page is OrderPageData => Boolean(page))
    .map((page) => {
      const hours = workingHoursForDate(page, account, date);
      return hours ? boundsOfWorkingHours(hours, page.duration ?? 0) : null;
    })
    .filter((value): value is DayBounds => Boolean(value));
  if (bounds.length === 0) {
    return configDayBounds(config, account, date);
  }
  return {
    startSeconds: Math.min(...bounds.map((bound) => bound.startSeconds)),
    endSeconds: Math.max(...bounds.map((bound) => bound.endSeconds)),
  };
};

/**
 * How long a session lasts in this area, in slots — the window the headline stat must hold free.
 * Derived from the duration of the pages that can actually book the area; static pages (a group
 * booking locks the whole room) don't describe a walk-in session, so they are only a last resort.
 */
export const sessionSlotsForArea = (
  area: AreaOccupancyData,
  config: SchedulerConfigurationData | undefined,
  account: AccountData | undefined,
  slotMinutes: number,
): number => {
  const slotSeconds = slotMinutes * SECONDS_PER_MINUTE;
  const fallbackSlots = Math.ceil((FALLBACK_SESSION_MINUTES * SECONDS_PER_MINUTE) / slotSeconds);
  if (!config) {
    return fallbackSlots;
  }

  const intersecting = (config.allowed_masks ?? []).filter((allowedMask) =>
    masksIntersect(allowedMask.mask, area.mask),
  );
  const bookable = intersecting.filter(
    (allowedMask) => allowedMask.resource_calculation_mode !== ResourceCalculationMode.STATIC,
  );
  const candidates = bookable.length > 0 ? bookable : intersecting;

  const durations = candidates
    .map((allowedMask) => getOrderPage(account, allowedMask.order_page_id) as OrderPageData | undefined)
    .filter((page): page is OrderPageData => Boolean(page))
    .map((page) => page.duration ?? 0)
    .filter((duration) => duration > 0);

  if (durations.length === 0) {
    return fallbackSlots;
  }
  return Math.max(1, Math.ceil(Math.max(...durations) / slotSeconds));
};

/** The page whose colour and unit words represent the area in the pillar header. */
export const primaryPageForArea = (
  area: AreaOccupancyData,
  config: SchedulerConfigurationData | undefined,
  account: AccountData | undefined,
): OrderPageData | undefined => {
  if (!config) {
    return undefined;
  }
  const intersecting = (config.allowed_masks ?? []).filter((allowedMask) =>
    masksIntersect(allowedMask.mask, area.mask),
  );
  const bookable = intersecting.filter(
    (allowedMask) => allowedMask.resource_calculation_mode !== ResourceCalculationMode.STATIC,
  );
  const chosen = (bookable.length > 0 ? bookable : intersecting)[0];
  return chosen ? (getOrderPage(account, chosen.order_page_id) as OrderPageData | undefined) : undefined;
};

/** Areas whose pages count tickets rather than tables read as "projects"/"stations". */
export const areaUsesTicketWording = (
  area: AreaOccupancyData,
  config: SchedulerConfigurationData | undefined,
): boolean =>
  (config?.allowed_masks ?? []).some(
    (allowedMask) =>
      masksIntersect(allowedMask.mask, area.mask) &&
      allowedMask.resource_calculation_mode === ResourceCalculationMode.TICKETS,
  );
